import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, Star, Check, Building2 } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { BrandMarquee } from "@/components/BrandMarquee";
import { PropertyCard } from "@/components/PropertyCard";
import { HERO_IMAGE, WHY_CHOOSE, GUEST_TYPES, STATS, REVIEWS, NEAR_EMPLOYERS } from "@/data/content";
import { UNITS } from "@/data/units";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative pt-28 md:pt-32 pb-12 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-2 h-2 rounded-full bg-[#8A9A86]" />
          <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#78716C]">
            16 Furnished Units · Austin, Texas
          </span>
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-[4.25rem] tracking-tighter font-light leading-[1.04] text-[#1C1917] max-w-5xl">
          <MaskedLines lines={["Thoughtfully Furnished Stays.", "Beautifully Managed."]} />
        </h1>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg sm:text-xl text-[#44403C] leading-relaxed font-light max-w-2xl"
          >
            From comfortable furnished rooms to private family homes and vacation homes,
            discover a place that feels like home in Austin, Texas.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="flex flex-wrap gap-4"
          >
            <Link data-testid="hero-view-units" to="/properties" className="rounded-full bg-[#1C1917] text-white px-8 py-3.5 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm">
              View Furnished Units
            </Link>
            <Link data-testid="hero-book-stay" to="/book" className="rounded-full bg-transparent border border-[#E7E5E4] text-[#1C1917] px-8 py-3.5 hover:bg-[#F5F5F4] transition-colors duration-300 font-medium text-sm">
              Book Your Stay
            </Link>
            <Link data-testid="hero-private-homes" to="/furnished-private-homes" className="inline-flex items-center gap-1.5 self-center text-sm font-medium text-[#8A9A86] hover:text-[#1C1917] transition-colors duration-300">
              Explore Furnished Private Homes <span aria-hidden>→</span>
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mt-14 relative rounded-2xl overflow-hidden aspect-[16/10] md:aspect-[16/8] border border-[#E7E5E4]"
        >
          <motion.img
            src={HERO_IMAGE}
            alt="A bright, professionally furnished Masaya living room in Austin"
            style={{ y, scale }}
            className="w-full h-[118%] object-cover -mt-[9%]"
          />
        </motion.div>
      </div>
    </section>
  );
};

const Intro = () => (
  <section data-testid="intro-section" className="px-6 md:px-12 py-24 md:py-32">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-24">
      <Reveal>
        <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">
          About our accommodations
        </span>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-tight text-[#1C1917]">
          16 professionally managed furnished units.
        </h2>
        <p className="mt-8 text-lg text-[#44403C] leading-relaxed font-light max-w-2xl">
          Masaya Hospitality offers professionally managed furnished private rooms within shared
          homes throughout the Austin area — including our newest units in Round Rock. Every unit
          is thoughtfully prepared to provide a clean, comfortable, and reliable stay for guests
          seeking quality accommodations for work, relocation, internships, healthcare assignments,
          or extended travel.
        </p>
      </Reveal>
    </div>
  </section>
);

const WhyChoose = () => (
  <section data-testid="why-choose-section" className="px-6 md:px-12 py-24 md:py-32 bg-[#F5F5F4] border-y border-[#E7E5E4]">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-14">
        <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Why Masaya</span>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1C1917]">Why choose Masaya</h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {WHY_CHOOSE.map((item, i) => {
          const Icon = Icons[item.icon] || Icons.Check;
          return (
            <Reveal key={item.label} delay={i * 0.05} y={16}>
              <div className="flex items-center gap-5 p-8 rounded-2xl bg-white border border-[#E7E5E4] h-full" data-testid={`why-${i}`}>
                <span className="w-12 h-12 rounded-full bg-[#E8ECE7] flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#748570]" strokeWidth={1.5} />
                </span>
                <span className="text-[#1C1917] text-lg font-normal tracking-tight">{item.label}</span>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Featured = () => (
  <section data-testid="featured-section" className="px-6 md:px-12 py-24 md:py-32">
    <div className="max-w-7xl mx-auto">
      <Reveal className="flex items-end justify-between gap-6 mb-14">
        <div>
          <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Our rooms</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1C1917]">Featured furnished rooms</h2>
        </div>
        <Link data-testid="featured-all-link" to="/properties" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#1C1917] hover:text-[#8A9A86] transition-colors duration-300">
          All units <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
        </Link>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {UNITS.slice(0, 6).map((p, i) => (
          <Reveal key={p.slug} delay={(i % 3) * 0.08}>
            <PropertyCard property={p} index={i} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section data-testid="stats-section" className="px-6 md:px-12 py-24 md:py-28 bg-[#1C1917]">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-14">
        <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">By the numbers</span>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-white">A managed operation you can rely on</h2>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-[#44403C] border border-[#44403C] rounded-2xl overflow-hidden">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.05} y={16}>
            <div className="bg-[#1C1917] p-8 md:p-10 h-full" data-testid={`stat-${i}`}>
              <p className="text-4xl md:text-5xl font-light tracking-tighter text-[#8A9A86]">{s.value}</p>
              <p className="mt-3 text-sm text-[#D6D3D1] font-light">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Guests = () => (  <section data-testid="guests-section" className="px-6 md:px-12 py-24 md:py-32">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-24">
      <Reveal>
        <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Our guests</span>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1C1917]">
          Trusted by professionals across Austin.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-1">
          {GUEST_TYPES.map((g) => (
            <div key={g} className="flex items-center gap-3 py-4 border-b border-[#E7E5E4]" data-testid={`guest-${g}`}>
              <Check className="w-4 h-4 text-[#8A9A86] shrink-0" strokeWidth={2} />
              <span className="text-[#1C1917] text-lg font-light">{g}</span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const NearEmployers = () => (
  <section data-testid="near-employers-section" className="px-6 md:px-12 py-24 md:py-32">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-14 max-w-2xl">
        <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Location</span>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1C1917]">Conveniently located near</h2>
        <p className="mt-6 text-lg text-[#44403C] leading-relaxed font-light">
          Convenient access to Austin's leading employers, universities, medical centers, and
          business districts.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {NEAR_EMPLOYERS.map((name, i) => (
          <Reveal key={name} delay={(i % 3) * 0.05} y={16}>
            <div className="flex items-center gap-4 p-7 rounded-2xl border border-[#E7E5E4] bg-white h-full" data-testid={`employer-${i}`}>
              <span className="w-11 h-11 rounded-full bg-[#E8ECE7] flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-[#748570]" strokeWidth={1.5} />
              </span>
              <span className="text-lg font-normal tracking-tight text-[#1C1917]">{name}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ReviewCard = ({ review, index }) => {
  const [expanded, setExpanded] = useState(false);
  const LONG = 240;
  const isLong = review.text.length > LONG;
  const shown = !isLong || expanded ? review.text : review.text.slice(0, LONG).trimEnd() + "…";
  return (
    <figure className="bg-white p-8 md:p-10 rounded-2xl border border-[#E7E5E4] h-full flex flex-col" data-testid={`review-${index}`}>
      <div className="flex items-center gap-1 mb-5" aria-label="5 out of 5 stars">
        {Array.from({ length: 5 }).map((_, s) => (
          <Star key={s} className="w-4 h-4 fill-[#8A9A86] text-[#8A9A86]" />
        ))}
      </div>
      <blockquote className="text-lg text-[#1C1917] leading-relaxed font-light">&ldquo;{shown}&rdquo;</blockquote>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`review-toggle-${index}`}
          className="mt-3 self-start text-sm font-medium text-[#8A9A86] hover:text-[#1C1917] transition-colors duration-300"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
      <figcaption className="mt-6 text-sm text-[#78716C]">
        <span className="font-medium text-[#1C1917]">{review.name}</span>
        {review.location ? <span> · {review.location}</span> : null}
        <span> · {review.source}</span>
      </figcaption>
    </figure>
  );
};

const Reviews = () => {
  const fiveStar = REVIEWS.filter((r) => r.rating === 5);
  return (
    <section data-testid="reviews-section" className="px-6 md:px-12 py-24 md:py-32 bg-[#F5F5F4] border-y border-[#E7E5E4]">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-14">
          <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">5-star guest reviews</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1C1917]">Loved by verified guests</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {fiveStar.map((r, i) => (
            <Reveal key={`${r.name}-${i}`} delay={(i % 2) * 0.06}>
              <ReviewCard review={r} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTA = () => (
  <section data-testid="cta-section" className="px-6 md:px-12 py-24 md:py-32">
    <div className="max-w-7xl mx-auto rounded-2xl bg-[#1C1917] px-8 py-16 md:p-20">
      <Reveal>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-white max-w-3xl">
          Whether you&apos;re staying or partnering — let&apos;s talk.
        </h2>
        <p className="mt-6 text-lg text-[#A8A29E] font-light max-w-xl">
          Inquire about availability for your next Austin stay, or partner with us to
          professionally manage your furnished property.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link data-testid="cta-longterm" to="/long-term-stays" className="rounded-full bg-[#8A9A86] text-white px-8 py-3.5 hover:bg-[#748570] transition-colors duration-300 font-medium text-sm">
            Request Long-Term Availability
          </Link>
          <Link data-testid="cta-partner" to="/owners" className="rounded-full bg-transparent border border-[#44403C] text-white px-8 py-3.5 hover:bg-[#292524] transition-colors duration-300 font-medium text-sm">
            Partner With Masaya
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const CATEGORIES = [
  {
    title: "Furnished Rooms",
    desc: "Comfortable, fully furnished rooms for professionals, interns, and extended stays.",
    to: "/properties",
    tag: "Available now",
    img: "https://images.unsplash.com/photo-1560185893-a55cbc8c57e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "A warmly furnished Masaya bedroom",
  },
  {
    title: "Furnished Private Homes",
    desc: "Entire fully furnished homes for families, relocations, and extended stays.",
    to: "/furnished-private-homes",
    tag: "Coming soon",
    img: "https://images.unsplash.com/photo-1615529182904-14819c35db37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "A cozy, light-filled family living room",
  },
  {
    title: "Vacation Homes",
    desc: "Exceptional vacation properties offering privacy, comfort, and elevated amenities.",
    to: "/luxury-vacation-homes",
    tag: "Coming soon",
    img: "https://images.unsplash.com/photo-1598928506311-c55ded91a20c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "An elevated, designer-furnished vacation home living room",
  },
];

const FindYourStay = () => (
  <section data-testid="find-your-stay-section" className="px-6 md:px-12 py-24 md:py-32 bg-[#F7F6F2] border-y border-[#E7E5E4]">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-14 max-w-2xl">
        <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Accommodations</span>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1C1917]">Find Your Perfect Stay</h2>
        <p className="mt-5 text-lg text-[#44403C] leading-relaxed font-light">
          Three distinct ways to stay with Masaya — choose the collection that fits your plans.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.to} delay={i * 0.08} y={20}>
            <Link
              to={c.to}
              data-testid={`stay-card-${i}`}
              className="group block h-full rounded-2xl overflow-hidden bg-white border border-[#E7E5E4] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(28,25,23,0.35)]"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={c.img} alt={c.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]" />
                <span className="absolute top-4 left-4 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[11px] font-medium tracking-wide text-[#44403C]">
                  {c.tag}
                </span>
              </div>
              <div className="p-6 lg:p-7">
                <h3 className="text-xl font-normal tracking-tight text-[#1C1917]">{c.title}</h3>
                <p className="mt-2.5 text-sm text-[#57534E] leading-relaxed font-light">{c.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#8A9A86] group-hover:text-[#1C1917] transition-colors duration-300">
                  Explore <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <FindYourStay />
      <Intro />
      <WhyChoose />
      <Featured />
      <Stats />
      <BrandMarquee />
      <Guests />
      <NearEmployers />
      <Reviews />
      <CTA />
    </>
  );
}
